'use client';

import type { CurrentBranding } from '@/hooks/use-current-branding';
import type { SessionValues } from '@/providers/session-provider/types';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

export const STORED_SESSIONS_KEY = 'pigello-stored-sessions-v2';

export type StoredSession = {
  session: SessionValues;
  branding: CurrentBranding | null;
};

/** A stored session is expired once its `tokenExp` (ISO string) has passed. */
function isExpired(stored: StoredSession): boolean {
  const exp = stored?.session?.tokenExp;
  if (!exp) {
    return true;
  }
  const expiresAt = new Date(exp);
  return Number.isNaN(expiresAt.getTime()) || expiresAt <= new Date();
}

/**
 * Reads, adds and removes stored sessions (e.g. for switching accounts),
 * persisted in localStorage. Sessions are unique by `session.userId`, and
 * reading filters out any that have expired according to their `tokenExp`.
 */
export function useStoredSessions() {
  const [storedSessions, setStoredSessions] = useLocalStorage<StoredSession[]>(
    STORED_SESSIONS_KEY,
    []
  );

  const sessions = storedSessions.filter((stored) => !isExpired(stored));

  const addSession = useCallback(
    (stored: StoredSession) => {
      setStoredSessions((prev) => [
        // Drop expired sessions and any existing entry for the same user.
        ...prev.filter(
          (s) => !isExpired(s) && s.session.userId !== stored.session.userId
        ),
        stored,
      ]);
    },
    [setStoredSessions]
  );

  const removeSession = useCallback(
    (stored: StoredSession) => {
      setStoredSessions((prev) =>
        prev.filter((s) => s.session.userId !== stored.session.userId)
      );
    },
    [setStoredSessions]
  );

  return { sessions, addSession, removeSession };
}
